/**
 * One audio proxy, decoded in segments around the playhead: a whole 5-minute
 * file is about 115 MB decoded and took 3 s to decode on the Android tablet.
 *
 * @module
 */
import type { MediaLike, Track } from "./controller";
import { range, readIndex, type ProxySource } from "./chain";
import type { FragmentIndex } from "./fragments";
import type { PlanFile } from "./plan";
import { ScheduledAudio } from "./scheduled-audio";

const NONE = { length: 0, start: () => 0, end: () => 0 };
/** Fragments (about 1 s each) per decoded segment. */
const SEGMENT_FRAGMENTS = 30;
/** The next segment is decoded once less than this is left... */
const AHEAD_S = 20;
/** ...and segments this far behind the playhead are freed. */
const BEHIND_S = 30;
/** Decoded audio ahead of the position that counts as ready to play. */
const READY_AHEAD_S = 0.5;

interface Source {
  index: FragmentIndex;
  init: ArrayBuffer;
  primingS: number;
}

export class LazyAudio implements MediaLike {
  readonly seeking = false;
  readonly track: Track;
  error: string | null = null;
  private source: Source | null = null;
  private inner: ScheduledAudio | null = null;
  private busy = false;
  private generation = 0;
  private abort = new AbortController();
  private pos = 0;
  private rate = 1;
  private wantPlaying = false;

  constructor(
    private readonly ctx: AudioContext,
    private readonly output: AudioNode,
    readonly file: PlanFile,
    private readonly proxies: ProxySource,
  ) {
    this.track = {
      media: this,
      startUtcMs: file.fromUtcMs,
      coverage: [{ fromUtcMs: file.fromUtcMs, toUtcMs: file.toUtcMs }],
      clockLocked: true,
    };
  }

  get readyState(): number {
    if (!this.inner) return 0;
    const pos = this.currentTime;
    const want = Math.min(pos + READY_AHEAD_S, this.duration);
    const b = this.inner.buffered;
    for (let i = 0; i < b.length; i++) if (b.start(i) <= pos + 0.001 && b.end(i) >= want) return 4;
    return 1;
  }

  get duration(): number {
    return this.inner?.duration ?? Infinity;
  }

  get buffered() {
    return this.inner?.buffered ?? NONE;
  }

  get paused(): boolean {
    return this.inner ? this.inner.paused : !this.wantPlaying;
  }

  get currentTime(): number {
    return this.inner?.currentTime ?? this.pos;
  }

  set currentTime(t: number) {
    this.pos = t;
    if (this.inner) this.inner.currentTime = t;
  }

  get playbackRate(): number {
    return this.rate;
  }

  set playbackRate(r: number) {
    this.rate = r;
    if (this.inner) this.inner.playbackRate = r;
  }

  play(): Promise<void> {
    this.wantPlaying = true;
    return this.inner?.play() ?? Promise.resolve();
  }

  pause(): void {
    this.wantPlaying = false;
    this.inner?.pause();
  }

  /** Call while the playhead is near: decodes what is missing around it, one segment at a time. */
  load(): void {
    if (this.busy || this.error) return;
    if (!this.source) {
      this.run(() => this.loadSource());
      return;
    }
    if (!this.inner) {
      const inner = new ScheduledAudio(this.ctx, this.output);
      inner.currentTime = this.pos;
      inner.playbackRate = this.rate;
      this.inner = inner;
      if (this.wantPlaying) void inner.play();
    }
    const pos = this.currentTime;
    this.inner.dropBefore(pos - BEHIND_S);
    const from = this.missingFrom(pos);
    if (from !== null) {
      const source = this.source;
      this.run((gen) => this.decode(source, from, gen));
    }
  }

  unload(): void {
    this.generation++;
    this.abort.abort();
    this.abort = new AbortController();
    this.busy = false;
    this.pos = this.currentTime;
    this.inner?.pause();
    this.inner = null;
  }

  private run(task: (gen: number) => Promise<void>): void {
    this.busy = true;
    const gen = this.generation;
    task(gen)
      .catch((e: unknown) => {
        if (gen === this.generation) this.error = String(e);
      })
      .finally(() => {
        if (gen === this.generation) this.busy = false;
      });
  }

  private async loadSource(): Promise<void> {
    const url = this.proxies.audioUrl(this.file.id);
    const signal = this.abort.signal;
    const [timing, index] = await Promise.all([this.proxies.timing(this.file.id), readIndex(url, signal)]);
    if (!index) throw new Error("audio proxy: no fragment index");
    const init = (await range(url, 0, index.initBytes, signal)).data;
    const rate = timing.audio_rate ?? 48_000;
    const shift = (timing.audio_start_s ?? 0) * 1000;
    if (this.source) return;
    this.source = { index, init, primingS: (timing.audio_encoder_delay_samples ?? 0) / rate };
    this.track.startUtcMs = this.file.fromUtcMs + shift;
    // The controller positions by startUtcMs; keep the position it set.
    this.pos = Math.max(0, this.pos - shift / 1000);
  }

  /** Where data is missing from `pos` to AHEAD_S ahead, or null. */
  private missingFrom(pos: number): number | null {
    const b = this.buffered;
    for (let i = 0; i < b.length; i++) {
      if (b.start(i) > pos + 0.001 || b.end(i) <= pos) continue;
      return b.end(i) - pos < AHEAD_S && b.end(i) < this.duration ? b.end(i) : null;
    }
    return pos < this.duration ? pos : null;
  }

  /**
   * Decodes the segment starting at the fragment holding track time `t`, from
   * one fragment earlier: an AAC decoder needs a frame before the first it
   * outputs correctly. Slices decode sample-aligned with the whole file.
   */
  private async decode(source: Source, t: number, gen: number): Promise<void> {
    const { index, init, primingS } = source;
    const frags = index.fragments;
    let a = 0;
    while (a + 1 < frags.length && (frags[a + 1]?.t ?? Infinity) - primingS <= t + 0.001) a++;
    const first = Math.max(0, a - 1);
    const last = Math.min(frags.length, a + SEGMENT_FRAGMENTS);
    const from = frags[first]?.offset ?? index.initBytes;
    const to = frags[last]?.offset ?? index.endBytes;
    const url = this.proxies.audioUrl(this.file.id);
    const data = (await range(url, from, to, this.abort.signal)).data;
    const bytes = new Uint8Array(init.byteLength + data.byteLength);
    bytes.set(new Uint8Array(init));
    bytes.set(new Uint8Array(data), init.byteLength);
    const buffer = await this.ctx.decodeAudioData(bytes.buffer);
    if (gen !== this.generation || !this.inner) return;
    const bufferStart = frags[first]?.t ?? 0;
    const playFrom = a === 0 ? bufferStart + primingS : (frags[a]?.t ?? 0);
    const segment = { startS: playFrom - primingS, skipS: playFrom - bufferStart, buffer };
    this.inner.add(segment);
    if (last === frags.length) this.inner.duration = segment.startS + buffer.duration - segment.skipS;
  }
}
